// tournamentsRepository.js
// API repository for tournaments-related backend calls
import authService from './authService.js';
import { buildApiUrl, defaultHeaders } from './apiUtils.js';

export const TournamentsRepository = {
    /**
     * @param {{ page?: number, perPage?: number }} [params]
     */
    async fetchTournaments(params = {}) {
        const page = params.page ?? 1;
        const perPage = params.perPage ?? 20;
        const qs = new URLSearchParams({ page: String(page), perPage: String(perPage) });
        const response = await fetch(buildApiUrl(`/tournaments?${qs}`), {
            method: 'GET',
            headers: defaultHeaders
        });
        if (!response.ok) throw new Error('Failed to fetch tournaments: ' + response.statusText);
        const data = await response.json();
        return {
            tournaments: data.tournaments || [],
            page: data.page ?? page,
            perPage: data.perPage ?? perPage,
            totalCount: data.totalCount ?? 0,
            totalPages: data.totalPages ?? 0
        };
    },

    async fetchTournament(tournamentId) {
        const response = await fetch(buildApiUrl(`/tournaments/${tournamentId}`));
        if (!response.ok) throw new Error('Failed to fetch tournament');
        return await response.json();
    },

    async createTournament(requestBody) {
        const response = await authService.authenticatedRequest(buildApiUrl('/tournaments/create'), {
            method: 'POST',
            body: JSON.stringify(requestBody)
        });
        if (!response.ok) throw new Error(await response.text());
        return await response.json();
    },

    async finishTournament(tournamentId) {
        const response = await authService.authenticatedRequest(buildApiUrl(`/tournaments/${tournamentId}/finish`), {
            method: 'POST'
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to finish tournament: ${response.status} ${response.statusText} - ${errorText}`);
        }
        
        const responseText = await response.text();
        if (!responseText) {
            return { success: true }; // Empty response is OK
        }
        
        try {
            return JSON.parse(responseText);
        } catch (e) {
            // If response is not JSON, return success with the text
            return { success: true, message: responseText };
        }
    },

    async generateNextRound(tournamentId) {
        const response = await authService.authenticatedRequest(buildApiUrl(`/tournaments/${tournamentId}/next-round`), {
            method: 'POST'
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to generate next round: ${response.status} ${response.statusText} - ${errorText}`);
        }
        
        return await response.json();
    },

    async deleteTournament(tournamentId) {
        const response = await authService.authenticatedRequest(buildApiUrl(`/tournaments/${tournamentId}`), {
            method: 'DELETE'
        });
        
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to delete tournament: ${response.status} ${response.statusText} - ${errorText}`);
        }
        
        return { success: true };
    }
};

export default TournamentsRepository;
